import { defineStore } from 'pinia';
import { SongsArray } from '@/types/modalStoreTypes';

export const ModalState = {
  OPEN: true,
  CLOSED: false,
} as const;

type ModalStateType = boolean;

interface PlaylistModal {
  id: string;
  title: string;
  shareLink: string;
  shareLinkAccess: string;
  songs: SongsArray[];
}

interface ModalsState {
  [key: string]: {
    state: ModalStateType;
    context?: string;
    playlistModal?: PlaylistModal;
  };
}

export const useModalStore = defineStore({
  id: 'ModalStore',

  state: (): ModalsState => ({
    sharePlaylistModal: { state: ModalState.CLOSED },
  }),

  actions: {
    setModalContext(modalName: keyof ModalsState, context: string) {
      if (!this[modalName]) {
        console.warn(`Modal "${modalName}" is not defined.`);
        return;
      }
      this[modalName].context = context;
    },
    toggleModalState(modalName: keyof ModalsState, newState: ModalStateType) {
      if (this[modalName] === undefined) {
        console.warn(`Modal "${modalName}" is not defined.`);
        return;
      }
      if (newState === ModalState.OPEN || newState === ModalState.CLOSED) {
        this[modalName].state = newState;
      } else {
        console.warn(`New state "${newState}" is not a valid modal state.`);
      }
    },
    setPlaylistModalData(modalName: keyof ModalsState, data: PlaylistModal) {
      if (!this[modalName]) {
        console.warn(`Modal "${modalName}" is not defined.`);
        return;
      }
      if (!this[modalName].playlistModal) {
        this[modalName].playlistModal = {
          id: '',
          title: '',
          shareLink: '',
          shareLinkAccess: '',
          songs: [],
        };
      }

      const playlistModalData = this[modalName].playlistModal;
      if (playlistModalData) {
        playlistModalData.id = data.id;
        playlistModalData.title = data.title;
        playlistModalData.shareLink = data.shareLink;
        playlistModalData.shareLinkAccess = data.shareLinkAccess;
        playlistModalData.songs = data.songs;
      }
    },
    updatePlaylistModalData(
      modalName: keyof ModalsState,
      data: Partial<PlaylistModal>,
    ) {
      if (!this[modalName]?.playlistModal) {
        console.warn(
          `Modal "${modalName}" or its playlistModal is not defined.`,
        );
        return;
      }
      this[modalName].playlistModal = Object.assign(
        {},
        this[modalName].playlistModal,
        data,
      );
    },
    clearPlaylistModalData(modalName: keyof ModalsState) {
      if (!this[modalName]?.playlistModal) {
        console.warn(
          `Modal "${modalName}" or its playlistModal is not defined.`,
        );
        return;
      }
      this[modalName].playlistModal = {
        id: '',
        title: '',
        shareLink: '',
        shareLinkAccess: '',
        songs: [],
      };
    },
  },

  getters: {
    isModalOpen: (state) => {
      return (modalName: keyof ModalsState) =>
        state[modalName]?.state === ModalState.OPEN;
    },
    getModalInfo: (state) => {
      return (modalName: keyof ModalsState) => {
        return state[modalName];
      };
    },
  },
});
